import { env } from '@/env'
import { hasAcceptedAnalyticsConsent } from '@/shared/analytics/consent/analytics-consent'
import type {
  AnalyticsEventName,
  AnalyticsEventParams,
} from '@/shared/analytics/events'

const GA4_ID = env.VITE_GA4_ID
const ANALYTICS_DEBUG_ENABLED = env.VITE_ANALYTICS_DEBUG === 'true'

const isProduction = import.meta.env.PROD

const canUseAnalytics =
  Boolean(GA4_ID) && (isProduction || ANALYTICS_DEBUG_ENABLED)

let isGa4Initialized = false

declare global {
  interface Window {
    dataLayer?: IArguments[]
    gtag?: (...args: unknown[]) => void
  }
}

const canUseGa4 = (requireConsent: boolean): boolean => {
  return canUseAnalytics && (!requireConsent || hasAcceptedAnalyticsConsent())
}

export const initGa4 = (options?: { requireConsent?: boolean }): void => {
  const requireConsent = options?.requireConsent ?? true

  if (!canUseGa4(requireConsent) || !GA4_ID || isGa4Initialized) {
    return
  }

  const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`

  if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
    const script = document.createElement('script')

    script.async = true
    script.src = scriptSrc

    document.head.appendChild(script)
  }

  window.dataLayer = window.dataLayer || []

  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments)
    }
  }

  window.gtag('js', new Date())

  window.gtag('config', GA4_ID, {
    send_page_view: false,
    debug_mode: ANALYTICS_DEBUG_ENABLED,
  })

  isGa4Initialized = true
}

export const trackGa4Event = (
  eventName: AnalyticsEventName,
  params?: AnalyticsEventParams,
): void => {
  if (!canUseGa4(true) || !window.gtag) {
    return
  }

  window.gtag('event', eventName, params)
}

export const trackGa4PageView = (
  url: string,
  title?: string,
  options?: { requireConsent?: boolean },
): void => {
  const requireConsent = options?.requireConsent ?? true

  if (!canUseGa4(requireConsent) || !window.gtag || !GA4_ID) {
    return
  }

  window.gtag('event', 'page_view', {
    page_location: url,
    page_title: title ?? document.title,
    debug_mode: ANALYTICS_DEBUG_ENABLED,
  })
}
